/* eslint-disable indent */
import { EActivityType } from '@constants';
import { IActivity } from '@sdk/contracts';
import { Typography } from 'antd';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  FooterWrapper,
  IconWrapper,
  InfoWrapper,
  NameWrapper,
  SpanWrapper,
  WatchingWrapper,
  Wrapper
} from './Activity.styles';

import { DEFAULT_ANIMATION } from '@/components/Animated';
import { Icon } from '@/components/Icon';
import { ACTIVITY_ICONS } from '@/constants';
import { publish } from '@/events';
import { ESidebarTabs } from '@/pages/Student/Course/constants';
import { usePdfResource } from '@/pages/Student/Course/store';
import {
  useActivityTrack,
  useDeliverableByActivity,
  useLastQuizResultByQuizId,
  useMutateMySubscriptions
} from '@/store';
import { formatDuration } from '@/utils';

const { Text } = Typography;

type Props = {
  isVisible: boolean;
  isCurrent: boolean;
  activity: IActivity;
  sectionIndex: number;
  activityIndex: number;
  sectionId: string;
};

const MOTION_SCALE = 0.97;
const ACTIVITY_NAME_WIDTH = 240;

export const Activity: React.FC<Props> = ({
  isVisible,
  isCurrent,
  activity,
  sectionIndex,
  activityIndex,
  sectionId
}) => {
  const { subscription, getActivityTrackById, getCurrentActivity, setIsRecapModalOpen } =
    useActivityTrack();
  const { updateActivityTrack } = useMutateMySubscriptions();
  const { t } = useTranslation('StudentCourseTranslations');
  const navigateTo = useNavigate();
  const { activity: currentActivity } = getCurrentActivity();
  const { quizResult } = useLastQuizResultByQuizId(activity.resource?.quiz?._id);
  const { setViewerRef } = usePdfResource();
  const { deliverable } = useDeliverableByActivity(activity._id);
  const { pathname } = useLocation();

  if (!subscription || !isVisible) return null;

  const { course } = subscription;

  const { isCompleted } = getActivityTrackById(activity._id) || {};
  const { _id, type, name, resource, isLocked } = activity;

  const handleSelectActivity = () => {
    if (currentActivity?._id === activity._id || isLocked) return;
    setViewerRef();
    publish('HIDE_ACTIVITY_REVIEWER');
    updateActivityTrack(
      {
        subscriptionId: subscription._id,
        activityTrack: { course: course._id, activity: activity._id, section: sectionId }
      },
      {
        onSuccess: () => {
          const params = new URLSearchParams(window.location.search);
          params.set('activity', activity._id);
          params.set('activeTab', ESidebarTabs.CONTENT);
          navigateTo({ pathname, search: `?${params.toString()}` });
          setTimeout(() => setIsRecapModalOpen(false), 1000);
        }
      }
    );
  };

  if (!resource && type !== EActivityType.DELIVERABLE) return null;

  const getActivityDescription = () => {
    if (type === EActivityType.VIDEO) {
      const duration = resource?.file ? formatDuration(resource?.file.duration) : 0;
      return t('VIDEO_DESCRIPTION', { duration });
    }
    if (type === EActivityType.PDF) return t('PDF_DESCRIPTION');
    if (type === EActivityType.QUIZ)
      return quizResult
        ? t('QUIZ_DESCRIPTION_WITH_SCORE', { score: quizResult.score.toFixed(1) })
        : t('QUIZ_DESCRIPTION', { questions: resource?.quiz?.questions.length });
    if (type === EActivityType.DELIVERABLE)
      return deliverable?.score
        ? t('DELIVERABLE_DESCRIPTION_WITH_SCORE', { score: deliverable.score.toFixed(1) })
        : deliverable
        ? t('DELIVERABLE_DESCRIPTION_SENT')
        : t('DELIVERABLE_DESCRIPTION');
    if (type === EActivityType.TEXT) return t('TEXT_DESCRIPTION');

    return undefined;
  };

  return (
    <motion.div whileHover={{ scale: MOTION_SCALE }} key={_id} style={{ width: '100%' }}>
      <Wrapper
        {...DEFAULT_ANIMATION}
        isCurrent={currentActivity?._id === _id}
        isLocked={isLocked}
        isCompleted={isCompleted}
        onClick={handleSelectActivity}>
        <IconWrapper isCurrent={isCurrent} isLocked={isLocked} isCompleted={isCompleted}>
          <Icon i={isLocked ? 'Locked' : ACTIVITY_ICONS[type]} />
        </IconWrapper>
        <InfoWrapper>
          <NameWrapper isCurrent={isCurrent} isLocked={isLocked}>
            <Text style={{ width: ACTIVITY_NAME_WIDTH }} ellipsis={{ tooltip: name }}>
              {`${sectionIndex + 1}.${activityIndex + 1}. ${name}`}
            </Text>
          </NameWrapper>
          <FooterWrapper>
            <SpanWrapper isCurrent={isCurrent} isLocked={isLocked}>
              {getActivityDescription()}
            </SpanWrapper>
            {isCompleted && <Icon i="Checkmark" color="green6" />}
            {isCurrent && (
              <WatchingWrapper>
                <Icon i="Pause" color="green6" />
                <div>{t('WATCHING_NOW')}</div>
              </WatchingWrapper>
            )}
          </FooterWrapper>
        </InfoWrapper>
      </Wrapper>
    </motion.div>
  );
};
